import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  translateText,
  SUPPORTED_LANGUAGES,
  LANGUAGE_FLAGS,
  LANGUAGE_NATIVE_NAMES,
  type LanguageCode,
} from "@/features/chat/services/translationService";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { returnHomeService } from "@/features/return-home/services/returnHomeService";
import { companyService } from "@/features/company/services/companyService";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
  deleteAttachmentFromAll,
} from "@/shared/services/uploadService";
import { announcementService } from "@/features/announcements/services/feedService";
import { getMimeType } from "@/shared/utils/fileHelpers";
import { useFormatDate } from "@/shared/hooks/useFormatDate";
import { formatDisplayName } from "@/shared/utils/formatters";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { Toast } from "@/shared/components/feedback/Toast";
import { AttachmentDropZone } from "@/features/announcements/components/AttachmentDropZone";
import { AttachmentList } from "@/features/announcements/components/AttachmentList";
import { CommentSection } from "@/features/announcements/components/CommentSection";
import { TripSummaryCard } from "@/features/return-home/components/TripSummaryCard";
import {
  COUNTRIES,
  AIRPORTS,
  getAirportsByCountry,
  type Airport,
} from "@/features/return-home/data/airports";
import type {
  ReturnHomeRecord,
  ReturnHomeFormData,
  ReturnHomeStatus,
} from "@/features/return-home/types/returnHome";
import type {
  AnnouncementAttachment,
  AttachmentUploadItem,
} from "@/features/announcements/types/feed";
import type { CompanyOption, EmployeeOption } from "@/features/inquiries/types/inquiry";

const OFFICER_TYPES = ["officer", "admin"];
// const TICKET_TYPE_KEYS = ["direct", "layover"] as const;

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const emptyForm: ReturnHomeFormData = {
  flight_date: "",
  return_date: "",
  route_origin: "",
  route_destination: "",
  ticket_type: "",
  return_type: -1,
  tio_jo: "",
  details: "",
  status: "Draft",
  resign_date: null,
  leave_days: null,
  mode_of_payment: null,
  payment_amount: null,
  currency: "JPY",
  payment_settled: false,
};

type ReturnHomeCategory =
  | "none"
  | "paid_leave"
  | "resignation"
  | "resignation_lumpsum"
  | "no_paid_leave"
  | "finished_contract";

const computeStatus = (
  flightDate: string,
  returnDate: string,
): ReturnHomeStatus => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const flight = flightDate ? new Date(flightDate) : null;
  const returnDt = returnDate ? new Date(returnDate) : null;

  if (!flight) return "";
  flight.setHours(0, 0, 0, 0);
  if (returnDt) returnDt.setHours(0, 0, 0, 0);

  if (today < flight) return "Upcoming";
  if (returnDt && today > returnDt) return "Returned";
  if (today >= flight) return "Out of Country";
  return "";
};

const computeStatusMessage = (
  flightDate: string,
  returnDate: string,
): { key: string; count: number } | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const flight = flightDate ? new Date(flightDate) : null;
  const returnDt = returnDate ? new Date(returnDate) : null;

  if (!flight) return null;
  flight.setHours(0, 0, 0, 0);
  if (returnDt) returnDt.setHours(0, 0, 0, 0);

  if (today < flight) {
    const days = Math.ceil(
      (flight.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    return { key: "return_home.status.days_until_departure", count: days };
  }
  if (returnDt && today > returnDt)
    return { key: "return_home.status.returned", count: 0 };
  if (returnDt && today >= flight) {
    const days = Math.ceil(
      (returnDt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    return { key: "return_home.status.days_until_return", count: days };
  }
  return null;
};

const STATUS_I18N_MAP: Record<string, string> = {
  Upcoming: "return_home.status.upcoming",
  "Out of Country": "return_home.status.out_of_country",
  Returned: "return_home.status.returned",
};

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  Upcoming: "info",
  "Out of Country": "warning",
  Returned: "success",
};

const SECTION = "space-y-4 pt-5 border-t border-gray-100 first:pt-0 first:border-t-0";
const SECTION_TITLE = "text-xs font-semibold text-gray-500 uppercase tracking-wide";

export const ReturnHomeFormPage = () => {
  const { t } = useTranslation();
  const { fmtDate } = useFormatDate();
  const navigate = useNavigate();
  const { id: recordId } = useParams<{ id: string }>();
  const { userData } = useAuth();
  const { canWrite } = usePermissions();
  const isOfficer = OFFICER_TYPES.includes(
    userData?.userType?.toLowerCase() || "",
  );
  const canApprove = canWrite("flight");

  const isEditing = !!recordId;

  // Page loading
  const [pageLoading, setPageLoading] = useState(isEditing);
  const [record, setRecord] = useState<ReturnHomeRecord | null>(null);

  // Form state
  const [form, setForm] = useState<ReturnHomeFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showSavedBanner, setShowSavedBanner] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const showToast = useCallback(
    (message: string, type: "success" | "error") => {
      if (type === "success") {
        setShowSavedBanner(true);
        setTimeout(() => setShowSavedBanner(false), 3000);
      } else {
        setToast({ message, type });
      }
    },
    [],
  );
  const [savedId, setSavedId] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  // Smart route selection
  const [originCountry, setOriginCountry] = useState("");
  const [destCountry, setDestCountry] = useState("");
  const [originAirports, setOriginAirports] = useState<Airport[]>([]);
  const [destAirports, setDestAirports] = useState<Airport[]>([]);

  // Company/Employee selection (officer creating on behalf of user)
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const employeeDropdownRef = useRef<HTMLDivElement>(null);

  // Category selection (drives return_type)
  const [category, setCategory] = useState<ReturnHomeCategory>("none");

  // Approval workflow (officer)
  const [approverRemarks, setApproverRemarks] = useState("");
  const [approving, setApproving] = useState(false);

  // Translation state
  const [translatedDetails, setTranslatedDetails] = useState<string | null>(null);
  const [translatedApproverComment, setTranslatedApproverComment] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeLang, setActiveLang] = useState<LanguageCode | null>(null);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const langPickerRef = useRef<HTMLDivElement>(null);

  const isTranslated = !!(translatedDetails || translatedApproverComment);

  // Attachments
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);
  const [uploads, setUploads] = useState<AttachmentUploadItem[]>([]);

  // Load record when editing
  useEffect(() => {
    if (!recordId) {
      setPageLoading(false);
      setForm(emptyForm);
      setSavedId(null);
      setAttachments([]);
      setUploads([]);
      return;
    }

    (async () => {
      try {
        const data = await returnHomeService.getById(recordId);
        if (data) {
          setRecord(data);
          populateForm(data);
        }
      } catch (err) {
        console.error("Failed to load return home record:", err);
      } finally {
        setPageLoading(false);
      }
    })();
  }, [recordId]);

  const populateForm = (rec: ReturnHomeRecord) => {
    const derivedCategory: ReturnHomeCategory =
      rec.return_type === 0 ? "paid_leave" :
      rec.return_type === 1 ? "resignation" :
      rec.return_type === 2 ? "resignation_lumpsum" :
      rec.return_type === 3 ? "no_paid_leave" :
      rec.return_type === 4 ? "finished_contract" : "none";
    setCategory(derivedCategory);

    setForm({
      id: rec.id,
      user_id: rec.user_id,
      flight_date: rec.flight_date ? rec.flight_date.slice(0, 10) : "",
      return_date: rec.return_date ? rec.return_date.slice(0, 10) : "",
      route_origin: rec.route_origin?.trim() || "",
      route_destination: rec.route_destination?.trim() || "",
      ticket_type: rec.ticket_type || "",
      return_type: rec.return_type ?? -1,
      tio_jo: rec.tio_jo || "",
      details: rec.details || "",
      status: rec.status || "Draft",
      resign_date: rec.resign_date ? rec.resign_date.slice(0, 10) : null,
      leave_days: rec.leave_days ?? null,
      mode_of_payment: rec.mode_of_payment ?? null,
      payment_amount: rec.payment_amount != null ? Number(rec.payment_amount) : null,
      currency: rec.currency || "JPY",
      payment_settled: rec.payment_settled ?? false,
    });
    setSavedId(rec.id);

    // Reverse-lookup countries from airport codes
    if (rec.route_origin) {
      const originCode = rec.route_origin.trim();
      const originAirport = AIRPORTS.find((a) => a.code === originCode);
      if (originAirport) {
        setOriginCountry(originAirport.country);
        setOriginAirports(getAirportsByCountry(originAirport.country));
      }
    }
    if (rec.route_destination) {
      const destCode = rec.route_destination.trim();
      const destAirport = AIRPORTS.find((a) => a.code === destCode);
      if (destAirport) {
        setDestCountry(destAirport.country);
        setDestAirports(getAirportsByCountry(destAirport.country));
      }
    }

    if (rec.attachments && Array.isArray(rec.attachments)) {
      const normalised = rec.attachments.map((a: any) => ({
        ...a,
        display_name: a.display_name || a.name || "",
        file_type: a.file_type || a.type || "",
      }));
      setAttachments(normalised);
    } else {
      setAttachments([]);
    }
  };

  // Load dropdowns for officers
  useEffect(() => {
    if (!isOfficer) return;
    companyService.getDropdown("flight_tracker").then(setCompanies).catch(console.error);
  }, [isOfficer]);

  // Load employees when company changes (officer flow)
  useEffect(() => {
    if (!selectedCompany || !isOfficer) {
      setEmployees([]);
      return;
    }
    companyService
      .getEmployeesByCompany(selectedCompany)
      .then((data: any[]) => {
        const normalized = data.map((emp: any) => ({
          value: emp.value || emp.user_id || emp.id,
          label:
            emp.label ||
            formatDisplayName(emp.last_name, emp.first_name, emp.middle_name),
        }));
        setEmployees(normalized);
      })
      .catch(() => setEmployees([]));
  }, [selectedCompany, isOfficer]);

  // Update airports when country changes
  useEffect(() => {
    setOriginAirports(originCountry ? getAirportsByCountry(originCountry) : []);
    if (!originCountry) setForm((prev) => ({ ...prev, route_origin: "" }));
  }, [originCountry]);

  useEffect(() => {
    setDestAirports(destCountry ? getAirportsByCountry(destCountry) : []);
    if (!destCountry) setForm((prev) => ({ ...prev, route_destination: "" }));
  }, [destCountry]);

  // Close language picker on outside click
  useEffect(() => {
    if (!langPickerOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        langPickerRef.current &&
        !langPickerRef.current.contains(e.target as Node)
      ) {
        setLangPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [langPickerOpen]);

  const handleTranslate = async (targetLang: LanguageCode) => {
    setLangPickerOpen(false);
    setIsTranslating(true);
    try {
      const [detailsResult, commentResult] = await Promise.all([
        form.details ? translateText(form.details, targetLang) : Promise.resolve(null),
        record?.approver_remarks ? translateText(record.approver_remarks, targetLang) : Promise.resolve(null),
      ]);
      if (detailsResult?.success) setTranslatedDetails(detailsResult.translatedText);
      if (commentResult?.success) setTranslatedApproverComment(commentResult.translatedText);
      setActiveLang(targetLang);
    } catch (err) {
      console.error("Translation error:", err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleShowOriginal = () => {
    setTranslatedDetails(null);
    setTranslatedApproverComment(null);
    setActiveLang(null);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(e.target as Node)
      ) {
        setCompanyDropdownOpen(false);
      }
      if (
        employeeDropdownRef.current &&
        !employeeDropdownRef.current.contains(e.target as Node)
      ) {
        setEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: false }));
    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else if (type === "number") {
      setForm((prev) => ({
        ...prev,
        [name]: value === "" ? null : Number(value),
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCategoryChange = (
    newCategory: ReturnHomeCategory,
  ) => {
    if (newCategory !== "none" && fieldErrors.category)
      setFieldErrors((prev) => ({ ...prev, category: false }));
    setCategory(newCategory);
    const isResignation = newCategory === "resignation" || newCategory === "resignation_lumpsum";
    const return_type =
      newCategory === "paid_leave" ? 0 :
      newCategory === "resignation" ? 1 :
      newCategory === "resignation_lumpsum" ? 2 :
      newCategory === "no_paid_leave" ? 3 :
      newCategory === "finished_contract" ? 4 : -1;
    setForm((prev) => ({
      ...prev,
      return_type,
      ...(newCategory === "finished_contract" && {
        return_date: "",
      }),
      ...(!isResignation && { resign_date: null }),
      ...(newCategory !== "paid_leave" && { leave_days: null }),
    }));
  };

  const handleCompanySelect = (companyId: string) => {
    setSelectedCompany(companyId);
    setForm((prev) => ({ ...prev, user_id: undefined }));
    setSelectedEmployee("");
    setCompanyDropdownOpen(false);
    setCompanySearch("");
  };

  const handleEmployeeSelect = (employeeId: string) => {
    setSelectedEmployee(employeeId);
    setForm((prev) => ({ ...prev, user_id: employeeId }));
    setEmployeeDropdownOpen(false);
    setEmployeeSearch("");
  };

  const handleSubmit = async (
    e: React.FormEvent,
    submitAs?: "draft" | "pending",
  ) => {
    e.preventDefault();
    setError("");

    const newFieldErrors: Record<string, boolean> = {};
    const requiresTravelDetails = category !== "finished_contract";
    const requiresPaymentDetails = category !== "finished_contract";

    // Required fields for Submit for Approval
    if (submitAs === "pending") {
      if (requiresTravelDetails && !form.flight_date) newFieldErrors.flight_date = true;
      if (requiresTravelDetails && !form.return_date) newFieldErrors.return_date = true;
      if (requiresTravelDetails && !form.route_origin) newFieldErrors.route_origin = true;
      if (requiresTravelDetails && !form.route_destination) newFieldErrors.route_destination = true;
    }

    // Category is required for all saves
    if (category === "none") newFieldErrors.category = true;

    // Category-specific validations
    if ((category === "resignation" || category === "resignation_lumpsum") && !form.resign_date) newFieldErrors.resign_date = true;
    if (category === "paid_leave" && (form.leave_days == null || form.leave_days === 0)) newFieldErrors.leave_days = true;
    // no_paid_leave and finished_contract: neither resign_date nor leave_days are required
    if (requiresPaymentDetails && isOfficer && (form.mode_of_payment === "company" || form.mode_of_payment === "svo_advanced") && (form.payment_amount == null || Number(form.payment_amount) < 0)) newFieldErrors.payment_amount = true;

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError(t("return_home.form.error_submit_required_fields"));
      const fieldOrder = ["flight_date", "return_date", "route_origin", "route_destination", "ticket_type", "category", "resign_date", "leave_days", "payment_amount"];
      const firstError = fieldOrder.find((f) => newFieldErrors[f]);
      if (firstError) {
        document.getElementById(`field-${firstError}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (form.flight_date) {
      const flight = new Date(form.flight_date);
      flight.setHours(0, 0, 0, 0);

      if (form.return_date) {
        const ret = new Date(form.return_date);
        ret.setHours(0, 0, 0, 0);
        if (ret <= flight) {
          setError(t("return_home.form.error_return_before_flight"));
          setFieldErrors({ return_date: true });
          document.getElementById("field-return_date")?.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }
    }

    // leave_days range validation
    if (category === "paid_leave" && form.leave_days != null && form.leave_days !== 0) {
      if (form.leave_days < 1 || form.leave_days > 99) {
        setError(t("return_home.form.error_leave_days_range"));
        setFieldErrors({ leave_days: true });
        document.getElementById("field-leave_days")?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }

    if (requiresPaymentDetails && isOfficer && (form.mode_of_payment === "company" || form.mode_of_payment === "svo_advanced") && (form.payment_amount == null || Number(form.payment_amount) < 0)) {
      setError(t("return_home.form.error_payment_amount_required"));
      setFieldErrors({ payment_amount: true });
      document.getElementById("field-payment_amount")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...form,
        leave_days: form.leave_days != null ? Number(form.leave_days) : null,
        payment_amount: form.payment_amount != null ? Number(form.payment_amount) : null,
      };

      if (category === "finished_contract") {
        dataToSave.return_type = 4;
        dataToSave.return_date = "";
        dataToSave.resign_date = null;
        dataToSave.leave_days = null;
      }

      // Set status based on action
      if (submitAs === "pending") {
        dataToSave.status = "Pending";
      } else if (!form.status || form.status === "") {
        dataToSave.status = "Draft";
      }

      if (isEditing && record) {
        await returnHomeService.update(record.id, dataToSave);
        setSavedId(record.id);
        if (submitAs === "pending") {
          const refreshed = await returnHomeService.getById(record.id);
          if (refreshed) {
            setRecord(refreshed);
            populateForm(refreshed);
          }
        }
      } else {
        const result = await returnHomeService.create(dataToSave);
        const newId = result.id;
        setSavedId(newId);
        navigate(`/return-home/${newId}`, { replace: true });
      }

      const toastMsg =
        submitAs === "pending"
          ? t("return_home.form.submitted")
          : t("return_home.form.saved");
      showToast(toastMsg, "success");
    } catch (err: any) {
      console.error("Failed to save return home:", err);
      const errMsg = err?.response?.data?.error || t("feed.form.save_error");
      setError(errMsg);
      showToast(errMsg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!record?.id) return;
    if (!window.confirm(t("common.alert.delete_confirm"))) return;
    try {
      await returnHomeService.delete(record.id);
      showToast(t("return_home.form.deleted"), "success");
      navigate("/return-home");
    } catch (err: any) {
      console.error("Failed to delete:", err);
      const errMsg = err?.response?.data?.error || t("common.error");
      showToast(errMsg, "error");
    }
  };

  const handleCancelSubmission = async () => {
    if (!record?.id) return;
    if (!window.confirm(t("return_home.form.cancel_submission_confirm")))
      return;
    try {
      await returnHomeService.patchStatus(record.id, "Draft");
      showToast(t("return_home.form.submission_cancelled"), "success");
      // Refresh the record
      const data = await returnHomeService.getById(record.id);
      if (data) {
        setRecord(data);
        populateForm(data);
      }
    } catch (err: any) {
      console.error("Failed to cancel submission:", err);
      const errMsg = err?.response?.data?.error || t("common.error");
      showToast(errMsg, "error");
    }
  };

  const handleApproval = async (newStatus: "Approved" | "Rejected") => {
    if (!record?.id) return;

    // Confirmation dialog
    const confirmMessage =
      newStatus === "Approved"
        ? t("return_home.form.approve_confirm_desc")
        : t("return_home.form.reject_confirm_desc");

    if (!window.confirm(confirmMessage)) return;

    setApproving(true);
    try {
      await returnHomeService.approve(record.id, {
        status: newStatus,
        approver_remarks: approverRemarks,
      });
      showToast(
        t("return_home.form.approval_success", { status: newStatus }),
        "success",
      );
      // Refresh the record
      const data = await returnHomeService.getById(record.id);
      if (data) {
        setRecord(data);
        populateForm(data);
      }
    } catch (err: any) {
      console.error("Failed to approve/reject:", err);
      const errMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t("common.error");
      showToast(errMsg, "error");
    } finally {
      setApproving(false);
    }
  };

  const handleCancelApproved = async () => {
    if (!record?.id) return;
    const reason = window.prompt(t("return_home.form.cancel_approved_prompt"));
    if (reason === null) return;
    if (!reason.trim()) {
      showToast(t("return_home.form.cancellation_reason_required"), "error");
      return;
    }

    setApproving(true);
    try {
      await returnHomeService.cancelApproved(record.id, reason.trim());
      showToast(t("return_home.form.approved_cancelled"), "success");
      const data = await returnHomeService.getById(record.id);
      if (data) {
        setRecord(data);
        populateForm(data);
      }
    } catch (err: unknown) {
      console.error("Failed to cancel approved application:", err);
      const apiError = err as {
        response?: { data?: { error?: string; message?: string } };
      };
      const errMsg =
        apiError.response?.data?.error ||
        apiError.response?.data?.message ||
        t("common.error");
      showToast(errMsg, "error");
    } finally {
      setApproving(false);
    }
  };

  // ── Attachment handlers ──
  const handleFilesSelected = (files: File[]) => {
    if (!savedId) return;

    for (const file of files) {
      const mimeType = getMimeType(file.name);
      if (!ACCEPTED_MIME_TYPES.includes(mimeType)) {
        setError(t("feed.form.invalid_file_type"));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t("feed.form.file_too_large"));
        continue;
      }

      const localId =
        typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : Date.now().toString() + Math.random().toString(36);

      const uploadItem: AttachmentUploadItem = {
        localId,
        file,
        status: "uploading",
        progress: 0,
      };

      setUploads((prev) => [...prev, uploadItem]);

      (async () => {
        try {
          const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(
            file.name,
            mimeType,
            "return_home",
            savedId,
          );

          await uploadToS3Direct(uploadUrl, file, mimeType, (progress) => {
            setUploads((prev) =>
              prev.map((u) => (u.localId === localId ? { ...u, progress } : u)),
            );
          });

          const confirmed = await confirmAttachmentInDB({
            relation_type: "return_home",
            relation_id: savedId,
            s3_key: s3Key,
            s3_bucket: bucketName,
            display_name: file.name,
            file_type: mimeType,
            file_size: file.size,
          });

          setUploads((prev) =>
            prev.map((u) =>
              u.localId === localId
                ? { ...u, status: "done", progress: 100, attachment: confirmed }
                : u,
            ),
          );

          setAttachments((prev) => [...prev, confirmed]);
          setTimeout(() => {
            setUploads((prev) => prev.filter((u) => u.localId !== localId));
          }, 1000);
        } catch (err: any) {
          setUploads((prev) =>
            prev.map((u) =>
              u.localId === localId
                ? { ...u, status: "error", error: err.message }
                : u,
            ),
          );
        }
      })();
    }
  };

  const handleAttachmentDelete = async (attachmentId: number | string) => {
    try {
      await deleteAttachmentFromAll(attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachment_id !== attachmentId),
      );
    } catch (err) {
      console.error("Failed to delete attachment:", err);
    }
  };

  const handleAttachmentRename = async (
    attachmentId: number | string,
    newName: string,
  ) => {
    try {
      await announcementService.renameAttachment(attachmentId, newName);
      setAttachments((prev) =>
        prev.map((a) =>
          a.attachment_id === attachmentId
            ? { ...a, display_name: newName }
            : a,
        ),
      );
    } catch (err) {
      console.error("Failed to rename attachment:", err);
    }
  };

  const handleCancelUpload = (localId: string) => {
    setUploads((prev) => prev.filter((u) => u.localId !== localId));
  };

  // Computed status
  const status = computeStatus(form.flight_date, form.return_date);
  const statusMsgData = computeStatusMessage(
    form.flight_date,
    form.return_date,
  );
  const statusMsg = statusMsgData
    ? t(statusMsgData.key, { count: statusMsgData.count })
    : "";

  const selectedCompanyLabel =
    companies.find((c) => c.value === selectedCompany)?.label || "";
  const selectedEmployeeLabel =
    employees.find((e) => e.value === selectedEmployee)?.label || "";

  // Officer creating a new record must pick company + employee first
  const officerSelectionRequired = isOfficer && !isEditing;
  const officerSelectionComplete =
    !officerSelectionRequired || (!!selectedCompany && !!selectedEmployee);

  // Disable fields based on status
  const isApprovedOrRejected =
    record && ["Approved", "Rejected", "Cancelled"].includes(record.status || "");
  const isPending = !!(record && record.status === "Pending");

  // Officers can edit all fields when Approved
  const officerApproved =
    isOfficer && !!(record && record.status === "Approved");

  // All fields locked for Approved, Rejected, and Pending (officers unlocked when Approved)
  const fieldDisabled =
    !officerSelectionComplete ||
    (!officerApproved && (!!isApprovedOrRejected || isPending));

  // Officers can still update Payment Settled and TIO/JO when Approved or Pending (not Rejected)
  const officerApprovedOrPending =
    isOfficer &&
    !!(record && ["Approved", "Pending"].includes(record.status || ""));
  const paymentTioDisabled = officerApprovedOrPending ? false : fieldDisabled;

  if (pageLoading) return <Spinner />;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Floating saved banner */}
      {showSavedBanner && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg text-sm font-medium pointer-events-none">
          <svg
            className="w-4 h-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          {t("return_home.form.saved_banner")}
        </div>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-3 max-w-6xl mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/return-home")}
              className="p-2 -ml-2 text-gray-400 hover:text-gray-600 active:text-gray-800 transition-colors rounded-lg shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </button>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">
              {isEditing
                ? t("return_home.edit_application")
                : t("return_home.new_application")}
              {isEditing && recordId && (
                <span className="ml-2 text-sm font-normal text-gray-400">#{recordId}</span>
              )}
            </h1>
          </div>
          {/* Button group - different based on status */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Translate button — only when editing */}
            {isEditing && (
              <div className="relative" ref={langPickerRef}>
                <button
                  type="button"
                  onClick={() => {
                    if (isTranslated) {
                      handleShowOriginal();
                    } else {
                      setLangPickerOpen((prev) => !prev);
                    }
                  }}
                  disabled={isTranslating}
                  title={isTranslated ? t("feed.show_original") : t("feed.translate")}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                    isTranslated
                      ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                >
                  {isTranslating ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  )}
                  {activeLang && !isTranslating && (
                    <span className="font-semibold">{activeLang.toUpperCase()}</span>
                  )}
                </button>
                {langPickerOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[200px] py-1 overflow-y-auto max-h-[60vh]">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => handleTranslate(lang.code as LanguageCode)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-base">{LANGUAGE_FLAGS[lang.code] || ""}</span>
                        <span>{LANGUAGE_NATIVE_NAMES[lang.code] || lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isEditing && record && record.status === "Draft" && (
              <>
                <Button
                  type="button"
                  onClick={(e: any) => {
                    e.preventDefault();
                    handleSubmit(
                      { preventDefault: () => {} } as any,
                      "pending",
                    );
                  }}
                  loading={saving}
                  disabled={fieldDisabled}
                  className="!bg-blue-600 hover:!bg-blue-700"
                >
                  {t("return_home.form.submit_for_approval")}
                </Button>
                <Button
                  type="submit"
                  form="return-home-form"
                  loading={saving}
                  disabled={fieldDisabled}
                >
                  {t("return_home.form.save")}
                </Button>
              </>
            )}
            {(!isEditing ||
              (isEditing &&
                record &&
                record.status !== "Draft" &&
                record.status !== "Pending")) && (
              <Button
                type="submit"
                form="return-home-form"
                loading={saving}
                disabled={
                  isEditing && officerApprovedOrPending
                    ? saving
                    : fieldDisabled
                }
              >
                {isEditing
                  ? t("return_home.form.update")
                  : t("return_home.form.save")}
              </Button>
            )}
            {isEditing && record && record.status === "Pending" && (
              <Button
                type="submit"
                form="return-home-form"
                loading={saving}
                disabled={fieldDisabled}
              >
                {t("return_home.form.update")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main layout: Form left, Sidebar right */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* ── LEFT COLUMN: Form ── */}
        <div className="flex-1 min-w-0">
          <div
            className={`bg-white rounded-xl border p-4 md:p-6 ${category === "resignation_lumpsum" ? "border-red-300 ring-2 ring-red-100" : "border-gray-200"}`}
          >
            {/* Lumpsum High Priority Alert */}
            {category === "resignation_lumpsum" && (
              <div className="flex items-center gap-2 px-3 py-2 mb-4 bg-red-50 text-red-800 border border-red-200 rounded-lg text-sm font-medium">
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                {t("return_home.form.lumpsum_alert")}
              </div>
            )}

            {/* Status indicator - always show application status */}
            {isEditing && record && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-sm font-medium text-gray-600">
                  {t("return_home.form.current_status")}:
                </span>
                <Badge
                  variant={
                    record.status === "Approved"
                      ? "success"
                      : record.status === "Rejected"
                        ? "danger"
                        : record.status === "Pending"
                          ? "warning"
                          : "default"
                  }
                >
                  {t(
                    `return_home.approval.status_${(record.status || "Draft").toLowerCase()}`,
                  )}
                </Badge>
              </div>
            )}

            {/* Travel status indicator (computed from dates) */}
            {status && !isEditing && (
              <div className="flex items-center gap-2 mb-4">
                <Badge variant={statusVariant[status] || "default"}>
                  {t(STATUS_I18N_MAP[status] || status)}
                </Badge>
                {statusMsg && (
                  <span className="text-sm text-gray-500">{statusMsg}</span>
                )}
              </div>
            )}

            {/* Trip summary (the right column holds comments once the record is saved) */}
            {savedId && (
              <div className="mb-4">
                <TripSummaryCard
                  origin={form.route_origin}
                  destination={form.route_destination}
                  flightDate={form.flight_date}
                  returnDate={form.return_date}
                  oneWay={category === "finished_contract"}
                />
              </div>
            )}

            <form
              id="return-home-form"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* Applicant */}
              {((isEditing && record) || (isOfficer && !isEditing)) && (
                <div className={SECTION}>
                  <h3 className={SECTION_TITLE}>{t("return_home.form.applicant_section")}</h3>
                  {/* User Profile & Visa Info (read-only, shown when editing) */}
                  {isEditing && record && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 mb-2">
                      <div>
                        <span className="text-xs font-medium text-gray-500">
                          {t("return_home.info.name")}
                        </span>
                        <p className="text-sm text-gray-800">
                          {record.user_name || "-"}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">
                          {t("return_home.info.company")}
                        </span>
                        <p className="text-sm text-gray-800">
                          {record.company_name_text || "-"}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">
                          {t("return_home.info.visa_type")}
                        </span>
                        <p className="text-sm text-gray-800">
                          {record.visa_type_descr || record.visa_type || "-"}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">
                          {t("return_home.info.joining_date")}
                        </span>
                        <p className="text-sm text-gray-800">
                          {record.joining_date ? fmtDate(record.joining_date) : "-"}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">
                          {t("return_home.info.visa_expiry")}
                        </span>
                        <p className="text-sm text-gray-800">
                          {record.visa_expiry_date
                            ? fmtDate(record.visa_expiry_date)
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">
                          {t("profile.country")}
                        </span>
                        <p className="text-sm text-gray-800">
                          {record.country ? t(`profile.country_${record.country.toLowerCase()}`, { defaultValue: record.country }) : "-"}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Officer: Company + Employee selection (for create) */}
                  {isOfficer && !isEditing && (
                    <>
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {t("return_home.form.select_company")}
                        </label>
                        <div className="relative" ref={companyDropdownRef}>
                          <input
                            type="text"
                            value={
                              companyDropdownOpen
                                ? companySearch
                                : selectedCompanyLabel
                            }
                            onChange={(e) => {
                              setCompanySearch(e.target.value);
                              if (!companyDropdownOpen)
                                setCompanyDropdownOpen(true);
                            }}
                            onFocus={() => {
                              setCompanyDropdownOpen(true);
                              setCompanySearch("");
                            }}
                            placeholder={t("return_home.form.search_company")}
                            className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          {selectedCompany && !companyDropdownOpen && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCompany("");
                                setSelectedEmployee("");
                                setForm((prev) => ({
                                  ...prev,
                                  user_id: undefined,
                                }));
                              }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                          {companyDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                              {companies
                                .filter((c) =>
                                  c.label
                                    ?.toLowerCase()
                                    .includes(companySearch.toLowerCase()),
                                )
                                .map((c) => (
                                  <button
                                    key={c.value}
                                    type="button"
                                    onClick={() => handleCompanySelect(c.value)}
                                    className={`w-full text-left px-3 py-2.5 md:py-2 text-base md:text-sm transition-colors ${
                                      selectedCompany === c.value
                                        ? "bg-indigo-50 text-indigo-700"
                                        : "text-gray-700 hover:bg-gray-50"
                                    }`}
                                  >
                                    {c.label}
                                  </button>
                                ))}
                              {companies.filter((c) =>
                                c.label
                                  ?.toLowerCase()
                                  .includes(companySearch.toLowerCase()),
                              ).length === 0 && (
                                <p className="px-3 py-2 text-xs text-gray-400 italic">
                                  {t("return_home.form.no_results")}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {selectedCompany && (
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            {t("return_home.form.select_employee")}
                          </label>
                          <div className="relative" ref={employeeDropdownRef}>
                            <input
                              type="text"
                              value={
                                employeeDropdownOpen
                                  ? employeeSearch
                                  : selectedEmployeeLabel
                              }
                              onChange={(e) => {
                                setEmployeeSearch(e.target.value);
                                if (!employeeDropdownOpen)
                                  setEmployeeDropdownOpen(true);
                              }}
                              onFocus={() => {
                                setEmployeeDropdownOpen(true);
                                setEmployeeSearch("");
                              }}
                              placeholder={t("return_home.form.search_employee")}
                              className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {employeeDropdownOpen && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                                {employees
                                  .filter((e) =>
                                    e.label
                                      ?.toLowerCase()
                                      .includes(employeeSearch.toLowerCase()),
                                  )
                                  .map((emp) => (
                                    <button
                                      key={emp.value}
                                      type="button"
                                      onClick={() =>
                                        handleEmployeeSelect(emp.value)
                                      }
                                      className={`w-full text-left px-3 py-2.5 md:py-2 text-base md:text-sm transition-colors ${
                                        selectedEmployee === emp.value
                                          ? "bg-indigo-50 text-indigo-700"
                                          : "text-gray-700 hover:bg-gray-50"
                                      }`}
                                    >
                                      {emp.label}
                                    </button>
                                  ))}
                                {employees.filter((e) =>
                                  e.label
                                    ?.toLowerCase()
                                    .includes(employeeSearch.toLowerCase()),
                                ).length === 0 && (
                                  <p className="px-3 py-2 text-xs text-gray-400 italic">
                                    {t("return_home.form.no_results")}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Trip: category first, since it decides which date fields apply */}
              <div className={SECTION}>
                <h3 className={SECTION_TITLE}>{t("return_home.form.trip_section")}</h3>
                {/* Category Dropdown */}
                <div id="field-category" className="space-y-1">
                  <label
                    className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : fieldErrors.category ? "text-red-600" : "text-gray-700"}`}
                  >
                    {t("return_home.form.category")}
                  </label>
                  <select
                    value={category}
                    onChange={(e) =>
                      handleCategoryChange(
                        e.target.value as ReturnHomeCategory,
                      )
                    }
                    disabled={fieldDisabled}
                    className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${fieldErrors.category ? "border-red-300 ring-2 ring-red-100" : "border-gray-300"}`}
                  >
                    <option value="none">
                      {t("return_home.form.category_none")}
                    </option>
                    <option value="paid_leave">
                      {t("return_home.form.category_paid_leave")}
                    </option>
                    <option value="no_paid_leave">
                      {t("return_home.form.category_no_paid_leave")}
                    </option>
                    <option value="resignation">
                      {t("return_home.form.category_resignation")}
                    </option>
                    <option value="resignation_lumpsum">
                      {t("return_home.form.category_resignation_two")}
                    </option>
                    <option value="finished_contract">
                      {t("return_home.form.category_finished_contract")}
                    </option>
                  </select>
                  {category === "paid_leave" && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t("return_home.form.leave_note_one")}
                    </p>
                  )}
                  {(category === "resignation" || category === "resignation_lumpsum") && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t("return_home.form.resignation_note_one")}
                    </p>
                  )}
                </div>

                {/* Resignation sub-fields (both types) */}
                {(category === "resignation" || category === "resignation_lumpsum") && (
                  <div className="space-y-3 pl-3 border-l-2 border-orange-200">
                    <div id="field-resign_date">
                      <Input
                        label={t("return_home.form.resign_date")}
                        name="resign_date"
                        type="date"
                        value={form.resign_date || ""}
                        onChange={handleChange}
                        disabled={fieldDisabled}
                        error={fieldErrors.resign_date ? " " : undefined}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {t("return_home.form.resignation_note_two")}
                    </p>
                  </div>
                )}

                {/* Paid Leave sub-fields */}
                {category === "paid_leave" && (
                  <div className="pl-3 border-l-2 border-blue-200">
                    <div id="field-leave_days">
                      <Input
                        label={t("return_home.form.leave_days")}
                        name="leave_days"
                        type="number"
                        value={
                          form.leave_days != null ? String(form.leave_days) : ""
                        }
                        onChange={handleChange}
                        disabled={fieldDisabled}
                        min="1"
                        max="99"
                        error={fieldErrors.leave_days ? " " : undefined}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div id="field-flight_date">
                    <Input
                      label={t("return_home.form.flight_date")}
                      name="flight_date"
                      type="date"
                      value={form.flight_date}
                      onChange={handleChange}
                      disabled={fieldDisabled}
                      error={fieldErrors.flight_date ? " " : undefined}
                    />
                  </div>
                  {category !== "finished_contract" && (
                    <div id="field-return_date">
                      <Input
                        label={t("return_home.form.return_date")}
                        name="return_date"
                        type="date"
                        value={form.return_date}
                        onChange={handleChange}
                        disabled={fieldDisabled}
                        min={
                          form.flight_date
                            ? new Date(new Date(form.flight_date).getTime() + 86400000)
                                .toISOString()
                                .slice(0, 10)
                            : new Date(Date.now() + 86400000).toISOString().slice(0, 10)
                        }
                        error={fieldErrors.return_date ? " " : undefined}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Route */}
              <div className={SECTION}>
                <h3 className={SECTION_TITLE}>{t("return_home.form.route_display")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                  <div className="sm:col-span-2">
                    <div id="field-route_origin" className="space-y-1">
                      <label
                        className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : fieldErrors.route_origin ? "text-red-600" : "text-gray-700"}`}
                      >
                        {t("return_home.form.origin_country")}
                      </label>
                      <select
                        value={originCountry}
                        onChange={(e) => { setOriginCountry(e.target.value); if (fieldErrors.route_origin) setFieldErrors((prev) => ({ ...prev, route_origin: false })); }}
                        disabled={fieldDisabled}
                        className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${fieldErrors.route_origin ? "border-red-300 ring-2 ring-red-100" : "border-gray-300"}`}
                      >
                        <option value="">
                          {t("return_home.form.select_country")}
                        </option>
                        {COUNTRIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    {originCountry && (
                      <div className="space-y-1">
                        <label
                          className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}
                        >
                          {t("return_home.form.origin_airport")}
                        </label>
                        <select
                          name="route_origin"
                          value={form.route_origin}
                          onChange={handleChange}
                          disabled={fieldDisabled}
                          className={`w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                        >
                          <option value="">
                            {t("return_home.form.select_airport")}
                          </option>
                          {originAirports.map((a) => (
                            <option key={a.code} value={a.code}>
                              {a.code} - {a.city} ({a.name})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                  <div className="sm:col-span-2">
                    <div id="field-route_destination" className="space-y-1">
                      <label
                        className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : fieldErrors.route_destination ? "text-red-600" : "text-gray-700"}`}
                      >
                        {t("return_home.form.dest_country")}
                      </label>
                      <select
                        value={destCountry}
                        onChange={(e) => { setDestCountry(e.target.value); if (fieldErrors.route_destination) setFieldErrors((prev) => ({ ...prev, route_destination: false })); }}
                        disabled={fieldDisabled}
                        className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${fieldErrors.route_destination ? "border-red-300 ring-2 ring-red-100" : "border-gray-300"}`}
                      >
                        <option value="">
                          {t("return_home.form.select_country")}
                        </option>
                        {COUNTRIES.filter((c) => c !== originCountry).map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    {destCountry && (
                      <div className="space-y-1">
                        <label
                          className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}
                        >
                          {t("return_home.form.dest_airport")}
                        </label>
                        <select
                          name="route_destination"
                          value={form.route_destination}
                          onChange={handleChange}
                          disabled={fieldDisabled}
                          className={`w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                        >
                          <option value="">
                            {t("return_home.form.select_airport")}
                          </option>
                          {destAirports.map((a) => (
                            <option key={a.code} value={a.code}>
                              {a.code} - {a.city} ({a.name})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Ticket Type */}
              {/* <div id="field-ticket_type" className="space-y-1">
                <label
                  className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : fieldErrors.ticket_type ? "text-red-600" : "text-gray-700"}`}
                >
                  {t("return_home.form.ticket_type")}
                </label>
                <select
                  name="ticket_type"
                  value={form.ticket_type}
                  onChange={handleChange}
                  disabled={fieldDisabled}
                  className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${fieldErrors.ticket_type ? "border-red-300 ring-2 ring-red-100" : "border-gray-300"}`}
                >
                  <option value="">
                    {t("return_home.form.select_ticket_type")}
                  </option>
                  {TICKET_TYPE_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {t(`return_home.form.ticket_${key}`)}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* Payment Details section */}
              <div className="space-y-3 pt-5 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("return_home.form.payment_section")}
                </p>
                <div className="space-y-1">
                  <label
                    className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}
                  >
                    {t("return_home.form.mode_of_payment")}
                  </label>
                  <select
                    name="mode_of_payment"
                    value={form.mode_of_payment || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        mode_of_payment:
                          (e.target.value as "company" | "self" | "svo_advanced") || null,
                        // Reset payment amount when switching away from company-like modes
                        ...(e.target.value !== "company" && e.target.value !== "svo_advanced" && {
                          payment_amount: null,
                        }),
                      }))
                    }
                    disabled={fieldDisabled}
                    className={`w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                  >
                    <option value="">—</option>
                    <option value="company">
                      {t("return_home.form.payment_company")}
                    </option>
                    <option value="self">
                      {t("return_home.form.payment_self")}
                    </option>
                    <option value="svo_advanced">
                      {t("return_home.form.payment_svo_advanced", { defaultValue: "Request ticket to SVO, to be billed to the company." })}
                    </option>
                  </select>
                  {form.mode_of_payment === "svo_advanced" && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t("return_home.form.svo_advanced_note", { defaultValue: "Note: The applicant will pay directly to the company." })}
                    </p>
                  )}
                </div>
                {(form.mode_of_payment === "company" || form.mode_of_payment === "svo_advanced") && isOfficer && (
                  <>
                    <div id="field-payment_amount" className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Input
                          label={t("return_home.form.payment_amount")}
                          name="payment_amount"
                          type="number"
                          value={
                            form.payment_amount != null
                              ? String(form.payment_amount)
                              : ""
                          }
                          onChange={handleChange}
                          disabled={paymentTioDisabled}
                          min="0"
                          error={fieldErrors.payment_amount ? " " : undefined}
                        />
                      </div>
                      <div className="space-y-1 shrink-0">
                        <label
                          className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}
                        >
                          {t("return_home.form.currency")}
                        </label>
                        <div className="px-3 py-2.5 md:py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-600 font-medium">
                          {form.currency || "JPY"}
                        </div>
                      </div>
                    </div>

                    {/* Payment Settled — visible when company pays and amount > 0 */}
                    {form.payment_amount != null &&
                      Number(form.payment_amount) > 0 && (
                        <div
                          className={`mt-3 rounded-lg border-2 px-4 py-3 flex items-center justify-between gap-4 ${
                            form.payment_settled
                              ? "bg-green-50 border-green-400"
                              : "bg-amber-50 border-amber-400"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xl ${form.payment_settled ? "text-green-500" : "text-amber-500"}`}
                            >
                              {form.payment_settled ? "✅" : "⏳"}
                            </span>
                            <div>
                              <p
                                className={`text-sm font-semibold ${form.payment_settled ? "text-green-800" : "text-amber-800"}`}
                              >
                                {t("return_home.form.payment_settled")}
                              </p>
                              <p
                                className={`text-xs ${form.payment_settled ? "text-green-600" : "text-amber-600"}`}
                              >
                                {form.payment_settled
                                  ? t("return_home.form.payment_settled_yes")
                                  : t("return_home.form.payment_settled_no")}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              disabled={paymentTioDisabled}
                              onClick={() =>
                                setForm((prev) => ({
                                  ...prev,
                                  payment_settled: false,
                                }))
                              }
                              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                                !form.payment_settled
                                  ? "bg-amber-500 text-white border-amber-500"
                                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                              } ${paymentTioDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              {t("return_home.form.payment_settled_no_btn")}
                            </button>
                            <button
                              type="button"
                              disabled={paymentTioDisabled}
                              onClick={() =>
                                setForm((prev) => ({
                                  ...prev,
                                  payment_settled: true,
                                }))
                              }
                              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                                form.payment_settled
                                  ? "bg-green-500 text-white border-green-500"
                                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                              } ${paymentTioDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              {t("return_home.form.payment_settled_yes_btn")}
                            </button>
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>

              {/* TIO / JO */}
              {isOfficer && (
                <Input
                  label={t("return_home.form.tio_jo")}
                  name="tio_jo"
                  value={form.tio_jo}
                  onChange={handleChange}
                  disabled={paymentTioDisabled}
                />
              )}

              {/* Details */}
              <div className="space-y-1">
                <label
                  className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}
                >
                  {t("return_home.form.details")}
                </label>
                {translatedDetails ? (
                  <>
                    <p className="px-3 py-2 text-sm text-gray-800 bg-indigo-50 border border-indigo-100 rounded-lg whitespace-pre-wrap">
                      {translatedDetails}
                    </p>
                    <p className="text-[10px] text-indigo-400 italic">
                      {t("feed.translated")}
                    </p>
                  </>
                ) : (
                  <textarea
                    name="details"
                    value={form.details}
                    onChange={handleChange}
                    disabled={fieldDisabled}
                    rows={4}
                    className={`w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                  />
                )}
              </div>

              {/* Approver Comment — visible to all users when editing */}
              {isEditing && record?.approver_remarks && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm">
                  <span className="text-gray-500">
                    {t("return_home.form.approver_comment")}:
                  </span>
                  {translatedApproverComment ? (
                    <>
                      <p className="text-gray-800 mt-1 whitespace-pre-wrap">
                        {translatedApproverComment}
                      </p>
                      <p className="text-[10px] text-indigo-400 italic mt-1">
                        {t("feed.translated")}
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-800 mt-1">
                      {record.approver_remarks}
                    </p>
                  )}
                </div>
              )}

              {/* Officer Approval Workflow (editing only, only for Pending/Approved/Rejected) */}
              {canApprove &&
                isEditing &&
                record &&
                record.status &&
                ["Pending", "Approved", "Rejected", "Cancelled"].includes(record.status) && (
                  <div className="space-y-3 pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {t("return_home.form.approval_section")}
                    </p>

                    {/* Approver remarks input - only show for Pending status */}
                    {record.status === "Pending" && (
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {t("return_home.form.approver_remarks")}
                        </label>
                        <textarea
                          value={approverRemarks}
                          onChange={(e) => setApproverRemarks(e.target.value)}
                          rows={2}
                          placeholder={t(
                            "return_home.form.remarks_placeholder",
                          )}
                          disabled={!!isApprovedOrRejected}
                          className={`w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${isApprovedOrRejected ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                        />
                      </div>
                    )}

                    {/* Approve / Reject buttons - only show for Pending status */}
                    {record.status === "Pending" && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={() => handleApproval("Approved")}
                          loading={approving}
                          disabled={(form.mode_of_payment === "company" || form.mode_of_payment === "svo_advanced") && (form.payment_amount == null || Number(form.payment_amount) < 0)}
                          className="flex-1 !bg-green-600 hover:!bg-green-700"
                        >
                          {t("return_home.form.approve")}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          onClick={() => handleApproval("Rejected")}
                          loading={approving}
                          className="flex-1"
                        >
                          {t("return_home.form.reject")}
                        </Button>
                      </div>
                    )}

                    {record.status === "Approved" && (
                      <Button
                        type="button"
                        variant="danger"
                        onClick={handleCancelApproved}
                        loading={approving}
                        className="w-full"
                      >
                        {t("return_home.form.cancel_approved")}
                      </Button>
                    )}
                  </div>
                )}

              {isEditing && record?.status === "Cancelled" && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-100 text-sm">
                  <p className="font-medium text-red-800">
                    {t("return_home.form.cancelled_label")}
                  </p>
                  <p className="mt-1 text-red-700 whitespace-pre-wrap">
                    {record.cancellation_reason}
                  </p>
                  {(record.cancelled_by_name || record.cancelled_by || record.cancelled_at) && (
                    <p className="mt-1 text-xs text-red-600">
                      {record.cancelled_by_name || record.cancelled_by || "-"}
                      {record.cancelled_at ? ` · ${fmtDate(record.cancelled_at)}` : ""}
                    </p>
                  )}
                </div>
              )}

              {/* Audit fields (read-only) */}
              {isEditing && record && (
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    {t("return_home.audit")}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">
                        {t("return_home.info.application_date")}:
                      </span>
                      <p className="text-gray-800">
                        {record.application_date
                          ? fmtDate(record.application_date)
                          : "-"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">
                        {t("return_home.info.created")}:
                      </span>
                      <p className="text-gray-800">
                        {record.created_at ? fmtDate(record.created_at) : "-"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">
                        {t("return_home.info.last_updated")}:
                      </span>
                      <p className="text-gray-800">
                        {record.updated_at ? fmtDate(record.updated_at) : "-"}
                      </p>
                    </div>
                    {record.approved_by && (
                      <div>
                        <span className="text-gray-500">
                          {t("return_home.info.approved_at")}:
                        </span>
                        <p className="text-gray-800">
                          {record.approved_at
                            ? fmtDate(record.approved_at)
                            : "-"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}

              {/* Delete button for Draft status (any user) */}
              {isEditing && record && record.status === "Draft" && (
                <div className="flex pt-2">
                  <Button
                    variant="danger"
                    type="button"
                    onClick={handleDelete}
                    className="w-full sm:w-auto"
                  >
                    {t("common.delete")}
                  </Button>
                </div>
              )}

              {/* Cancel Submission button for Pending status (officer or user) */}
              {isEditing && record && record.status === "Pending" && (
                <div className="flex pt-2">
                  <Button
                    variant="danger"
                    type="button"
                    onClick={handleCancelSubmission}
                    className="w-full sm:w-auto"
                  >
                    {t("return_home.form.cancel_submission")}
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* ── RIGHT COLUMN (new application): live trip preview, above the form on small screens ── */}
        {!savedId && (
          <div className="lg:w-96 order-first lg:order-none">
            <div className="lg:sticky lg:top-24">
              <TripSummaryCard
                origin={form.route_origin}
                destination={form.route_destination}
                flightDate={form.flight_date}
                returnDate={form.return_date}
                oneWay={category === "finished_contract"}
              />
            </div>
          </div>
        )}

        {/* ── RIGHT COLUMN: Comments & Attachments Sidebar ── */}
        {savedId && (
          <div className="lg:w-96 space-y-4">
            {/* Comments */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                {t("return_home.sidebar.comments")}
              </h3>
              <CommentSection
                announcementId={savedId}
                relationType="return_home"
                currentUserId={userData?.id ?? ""}
                currentUserName={formatDisplayName(userData?.lastName, userData?.firstName, userData?.middleName)}
                isOfficer={isOfficer}
              />
            </div>

            {/* Attachments */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                {t("return_home.sidebar.attachments")}
              </h3>
              <AttachmentDropZone
                onFilesSelected={handleFilesSelected}
                accept=".pdf,.jpg,.jpeg,.png,.gif"
                disabled={uploads.some((u) => u.status === "uploading")}
              />
              <div className="mt-3">
                <AttachmentList
                  attachments={attachments}
                  uploads={uploads}
                  onDelete={handleAttachmentDelete}
                  onRename={handleAttachmentRename}
                  onCancelUpload={handleCancelUpload}
                />
              </div>
              {attachments.length === 0 && uploads.length === 0 && (
                <p className="text-xs text-gray-400 italic text-center py-2">
                  {t("return_home.sidebar.no_attachments")}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
